// Disabling the following jsdoc ESLint rules only because this code is checked by TypeScript directly
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useId, useRef, useState } from 'react';
import {
  Formik, Form, Field, ErrorMessage,
} from 'formik';
import { useFocusFirstField } from 'lib/Hooks/useFocusFirstField';
import { required, validEmail } from '../Form/validators';
import { passwordRequirements } from '../Form/passwordRequirements';
import { PasswordRequirements } from '../Form/PasswordRequirements/index';
import styles from './styles.module.scss';

/**
 * @param {{ email: string; password: string; agreedToTerms: boolean }} values
 */
function validate(values) {
  const errors = {};

  // Email
  const emailValidators = [required('Please enter a valid email'), validEmail];
  const emailError = emailValidators
    .map((validator) => validator(values.email))
    .find((error) => Boolean(error));

  if (emailError) {
    errors.email = emailError;
  }

  // Password
  const passwordError = passwordRequirements
    .map((rule) => rule.validator(values.password))
    .find((error) => Boolean(error));
  if (passwordError) {
    errors.password = passwordError;
  }

  // Terms
  const termsError = required('Please agree to the following terms to continue')(
    values.agreedToTerms,
  );
  if (termsError) {
    errors.agreedToTerms = termsError;
  }

  return errors;
}

/**
 * @typedef {object} SignupFormProps
 * @property {(email: string) => void} onSuccess - Callback when signup is successful
 */

/**
 * Signup form component
 * @param {SignupFormProps} props
 */
export function SignupForm(props) {
  const { onSuccess } = props;
  const idPrefix = useId();
  /**
   *
   * @param {string} s
   */
  const id = (s) => `${idPrefix}-${s}`;
  const formRef = useRef(null);
  const [submissionError, setSubmissionError] = useState('');

  useFocusFirstField(formRef);

  return (
    <div className={styles.signupForm}>
      <h1>Create Account</h1>
      <p className={styles.instructions}>
        Enter the email and password that will be associated with this account
      </p>

      <Formik
        initialValues={{ email: '', password: '', agreedToTerms: false }}
        validate={validate}
        validateOnMount
        onSubmit={(values, { setSubmitting }) => {
          fetch('/auth/users/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              first_name: '',
              last_name: '',
              email: values.email,
              password: values.password,
              source: '',
              brand: 'brand',
            }),
          })
            .then(() => onSuccess(values.email))
            .catch(() => setSubmissionError('Submission failed. Please try again.'))
            .finally(() => setSubmitting(false));
        }}
      >
        {({
          isSubmitting, isValid, touched, errors, values,
        }) => (
          <Form ref={formRef} noValidate>
            {submissionError && <div>{submissionError}</div>}

            <label htmlFor={id('email')}>Email</label>
            <Field
              id={id('email')}
              name="email"
              type="email"
              required
              maxLength={500}
              aria-describedby={id('email-error')}
              aria-invalid={touched.email && !!errors.email}
            />
            <div className={styles.validationError} aria-live="assertive" id={id('email-error')}>
              <ErrorMessage name="email" />
            </div>

            <label htmlFor={id('password')}>Password</label>
            <Field
              id={id('password')}
              name="password"
              type="password"
              required
              maxLength={500}
              aria-describedby={id('password-error')}
              aria-invalid={touched.password && !!errors.password}
            />
            <div className={styles.validationError} aria-live="assertive" id={id('password-error')}>
              <ErrorMessage name="password" />
            </div>

            <PasswordRequirements password={values.password} requirements={passwordRequirements} />

            <div
              className={styles.validationError}
              aria-live="assertive"
              id={id('agreed-to-terms-error')}
            >
              <ErrorMessage name="agreedToTerms" />
            </div>

            <div className={styles.checkbox}>
              <Field
                id={id('agreedToTerms')}
                name="agreedToTerms"
                type="checkbox"
                required
                aria-describedby={id('agreed-to-terms-error')}
                aria-invalid={touched.agreedToTerms && !!errors.agreedToTerms}
              />
              <label htmlFor={id('agreedToTerms')}>
                By checking this box and tapping Create Account, I agree to NBCUniversal&apos;&nbsp;
                <a
                  href="https://www.nbc.com/general/pages/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                &nbsp;and&nbsp;
                <a
                  href="https://www.nbcuniversal.com/privacy?intake=NBC_News"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                &nbsp;and that NBCUniversal and its sservice providers may process my information to
                provide me personalized content, recommendations, latest news, promotions and more
              </label>
            </div>

            <button type="submit" disabled={isSubmitting || !isValid}>
              Create Account
            </button>

            <footer>
              <p>
                Trouble logging in?
                {' '}
                <a href="/">Reset Your Password</a>
              </p>
              <p>Powered by NBCUniversal Profile</p>
            </footer>
          </Form>
        )}
      </Formik>
    </div>
  );
}
